import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Printer, MapPin } from 'lucide-react';
import { Card, Button, LoadingSpinner, ErrorMessage } from '@/components/ui';
import { inventoryApi } from '@/services/api/inventoryApi';
import { queryKeys } from '@/services/api/queryClient';
import { cn } from '@/lib/utils';

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

const DeliverySheetPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const sheetId = Number(id);

  const { data: sheet, isLoading, error } = useQuery({
    queryKey: queryKeys.deliverySheet(sheetId),
    queryFn: () => inventoryApi.getDeliverySheet(sheetId),
    enabled: !isNaN(sheetId),
  });

  if (isLoading) return <LoadingSpinner size="lg" className="py-24" />;
  if (error || !sheet) return <ErrorMessage message="Лист выдачи не найден" />;

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center gap-4 print:hidden">
        <button
          onClick={() => navigate('/inventory')}
          className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Лист выдачи #{sheet.id}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Поставка #{sheet.consolidated_delivery_id} • {formatDate(sheet.generated_at)}
          </p>
        </div>
        <Button variant="outline" onClick={() => window.print()}>
          <Printer className="w-4 h-4" />
          Печать
        </Button>
      </div>

      <div className="print:block">
        <div className="hidden print:block mb-6 text-center">
          <h1 className="text-xl font-bold">Лист выдачи #{sheet.id}</h1>
          <p className="text-sm text-gray-500">Дата: {formatDate(sheet.generated_at)}</p>
        </div>

        <Card>
          <Card.Body className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">№</th>
                  <th className="px-4 py-3 text-left font-semibold">Заказ</th>
                  <th className="px-4 py-3 text-left font-semibold">Клиент</th>
                  <th className="px-4 py-3 text-left font-semibold">Полка</th>
                  <th className="px-4 py-3 text-left font-semibold">Статус</th>
                  <th className="px-4 py-3 text-center font-semibold print:block hidden">Подпись</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sheet.items.map((item, idx) => (
                  <tr key={item.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">#{item.order_id}</td>
                    <td className="px-4 py-3 text-gray-700">{item.customer_name}</td>
                    <td className="px-4 py-3">
                      {item.shelf_location ? (
                        <span className="inline-flex items-center gap-1 text-sm">
                          <MapPin className="w-3.5 h-3.5 text-gray-400 print:hidden" />
                          {item.shelf_location}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                        item.status === 'ARRIVED' ? 'bg-blue-100 text-blue-700'
                          : item.status === 'COMPLETED' ? 'bg-green-100 text-green-700'
                          : item.status === 'PARTIALLY_HANDED_OFF' ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-600',
                      )}>
                        {item.status === 'PARTIALLY_HANDED_OFF' ? 'Частично выдан' : item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center print:block hidden">
                      <div className="w-24 border-b border-gray-300 mx-auto" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default DeliverySheetPage;
